import React, { useCallback, useEffect, useRef, useState } from "react";
import SumsubWebSdk from "@sumsub/websdk-react";
import orendaService from "../../services/orenda.service";
import { parseSumsubTokenResponse } from "./orendaApplicationHelpers";
import { orendaUserManager } from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";

// IDENTITY_VERIFICATION onboarding step. Fetches a Sumsub webtoken from our
// backend and mounts the official Sumsub Web SDK. The SDK handles all identity
// capture (documents, selfie, liveness). We watch its onMessage stream to know
// when the user has finished so we can:
//   1. Hide the SDK iframe (its own "You may close this page" screen is a
//      dead-end inside our embedded flow)
//   2. Show our own "Verified — Continue" screen with an explicit CTA
//   3. Poll viewapplication in the background in case the backend finishes
//      asynchronously (webhook delay)

// Event names that signal the user has actually finished (or at least
// submitted) their documents. CRITICAL: do NOT include "applicantloaded"
// — that event fires when the SDK boots up with an existing applicant,
// NOT when the user has done anything. Treating it as completion sends
// us to the "Verification submitted" screen the moment they land on the
// page and triggers the background-poll loop you saw.
//
// Case-insensitive substring match on the event type.
const COMPLETION_HINTS = [
  "submitted",
  "applicantstatus",
  "verified",
  "resubmitted",
  // "complete" alone is also too generic (fires on sub-step completions).
  // Rely on the status-field branch below for GREEN / completed / verified.
];

const looksLikeCompletion = (type, payload) => {
  if (typeof type === "string") {
    const t = type.toLowerCase();
    if (COMPLETION_HINTS.some((frag) => t.includes(frag))) return true;
  }
  if (payload && typeof payload === "object") {
    const status = String(
      payload.reviewStatus ||
        payload.reviewResult?.reviewAnswer ||
        payload.applicantStatus ||
        ""
    ).toLowerCase();
    // GREEN = passed. "completed" / "verified" / "approved" are the terminal
    // positive states — anything else (pending, init, onhold, red) is NOT a
    // completion we should surface.
    if (status && /^(completed|green|approved|verified)$/.test(status)) return true;
  }
  return false;
};

// Narrower: Sumsub explicitly says the review result was approved. When we
// see this, surface a slightly happier "verified" message.
const isGreen = (payload) =>
  payload?.reviewResult?.reviewAnswer === "GREEN" ||
  payload?.reviewStatus === "completed" ||
  payload?.applicantStatus === "GREEN";

const POLL_MS = 5000;
// Cap on how many silent background polls we fire before giving up and
// leaving the user on the Continue screen. 24 polls × 5s = 2 minutes —
// matches the "most reviews finish in under a minute" copy and avoids
// pounding the backend forever on a sandbox check that will never flip.
const MAX_POLLS = 24;
// After this long on the Sumsub screen, show the always-visible Continue bar
// as a last-resort escape hatch so no user is ever stranded.
const FALLBACK_CONTINUE_MS = 30_000;

const OrendaSumsub = ({ onAdvance }) => {
  const [phase, setPhase] = useState("loading"); // loading | ready | submitted | error
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [verified, setVerified] = useState(false);
  const [checkingAgain, setCheckingAgain] = useState(false);
  const [showFallbackBar, setShowFallbackBar] = useState(false);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const fallbackTimerRef = useRef(null);

  const fetchToken = useCallback(async () => {
    setPhase("loading");
    setError(null);
    try {
      const response = await orendaService.getSumsubTokenOrenda();
      const parsed = parseSumsubTokenResponse(response);
      if (!parsed.token) throw new Error("Could not obtain a verification token.");
      setToken(parsed.token);
      setPhase("ready");
      return parsed.token;
    } catch (err) {
      if (err?.code === "SSO_SESSION_EXPIRED") {
        setSessionExpired(true);
      } else {
        setError(err?.response?.data?.errorMessage || err?.message || String(err));
      }
      setPhase("error");
      return null;
    }
  }, []);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  const handleTokenExpired = useCallback(async () => await fetchToken(), [fetchToken]);

  const startBackgroundPoll = useCallback(() => {
    if (pollRef.current) return;
    pollCountRef.current = 0;
    pollRef.current = window.setInterval(() => {
      // Pass {silent:true} so OrendaLanding refetches application without
      // flipping its phase to "fetching-app" — which would unmount this
      // whole component (SDK iframe included) and leave the user stranded.
      try { onAdvance?.({ silent: true }); } catch { /* ignore, keep polling */ }
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS && pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_MS);
  }, [onAdvance]);

  const handleMessage = useCallback(
    (type, payload) => {
      // Log every event so if an unexpected name is firing we can see it in
      // DevTools and expand the matcher.
      // eslint-disable-next-line no-console
      console.log("[sumsub]", type, payload);
      if (!looksLikeCompletion(type, payload)) return;
      // Only treat this as completion if Sumsub tells us the review is
      // actually DONE — green/approved/verified status. We do NOT trust
      // Sumsub events alone to flip the UI to "Verification submitted":
      // the SDK fires events like applicantSubmitted / applicantResubmitted
      // throughout a session (not just at the end), and we were ending up
      // on the wait screen while the backend still had
      // currentStep=IDENTITY_VERIFICATION. The source of truth for "KYC is
      // done" is the backend; this handler just hints at it, and only when
      // the payload is unambiguously positive.
      if (!isGreen(payload)) return;
      setPhase("submitted");
      setVerified(true);
      startBackgroundPoll();
    },
    [startBackgroundPoll]
  );

  const handleError = useCallback((err) => {
    // eslint-disable-next-line no-console
    console.error("[sumsub] error", err);
    setError(err?.reason || err?.message || "Verification widget error.");
    setPhase("error");
  }, []);

  // Fallback escape hatch: once the SDK has been mounted and ready for a
  // while, show a persistent Continue bar so the user is never stranded on
  // Sumsub's "You may close this page" screen if events don't fire.
  useEffect(() => {
    if (phase !== "ready") return undefined;
    if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = window.setTimeout(() => {
      setShowFallbackBar(true);
    }, FALLBACK_CONTINUE_MS);
    return () => {
      if (fallbackTimerRef.current) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [phase]);

  useEffect(
    () => () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (fallbackTimerRef.current) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    },
    []
  );

  const reAuthenticate = () => {
    orendaUserManager.signinRedirect({
      extraQueryParams: {
        identity_provider: orendaOidcConfig.identity_provider,
        prompt: "login",
      },
    });
  };

  const checkAgain = async () => {
    setCheckingAgain(true);
    // Silent refetch — only swap screens if the backend has actually
    // moved the step forward. If we're still on IDENTITY_VERIFICATION
    // (common in sandbox where approval is manual), we stay here instead
    // of flashing the "Loading your application…" state and remounting.
    try { await onAdvance?.({ silent: true }); } catch { /* ignore */ }
    window.setTimeout(() => setCheckingAgain(false), 400);
  };

  return (
    <>
      <h2 className="orenda-heading">
        {phase === "submitted" ? "Verification submitted" : "Verify your identity"}
      </h2>
      <p className="orenda-subheading">
        {phase === "submitted"
          ? "Thanks — we've received your details and are finalising the check."
          : "We'll guide you through document upload and a short selfie check. Have your ID document ready."}
      </p>

      {sessionExpired && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Your session expired.</strong>
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            Sign in again to continue verification.
          </div>
          <button onClick={reAuthenticate} className="orenda-btn-primary">
            Sign in again
          </button>
        </div>
      )}

      {!sessionExpired && phase === "loading" && (
        <div className="orenda-card">Preparing your verification session…</div>
      )}

      {!sessionExpired && phase === "error" && (
        <>
          <div className="orenda-alert orenda-alert--warn">
            <strong>Could not start identity verification.</strong>
            <div>{error}</div>
          </div>
          <button onClick={fetchToken} className="orenda-btn-primary">
            Try again
          </button>
        </>
      )}

      {!sessionExpired && phase === "ready" && token && (
        <>
          <div style={sumsubHostStyle}>
            <SumsubWebSdk
              accessToken={token}
              expirationHandler={handleTokenExpired}
              config={{ lang: "en" }}
              options={{ addViewportTag: false, adaptIframeHeight: true }}
              onMessage={handleMessage}
              onError={handleError}
            />
          </div>
          {showFallbackBar && (
            <div className="orenda-card" style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 14, color: "#4a4d55" }}>
                Already finished verification?
              </div>
              <button
                onClick={async () => {
                  // Silent refetch: if the backend has moved us off
                  // IDENTITY_VERIFICATION, StepRouter will swap screens for
                  // us. If not, we stay on the Sumsub iframe without
                  // flashing "Verification submitted" at a user whose KYC
                  // is still pending.
                  await onAdvance?.({ silent: true });
                }}
                className="orenda-btn-primary"
              >
                Check status
              </button>
            </div>
          )}
        </>
      )}

      {!sessionExpired && phase === "submitted" && (
        <div className="orenda-card" style={{ textAlign: "center", padding: "32px 24px" }}>
          <div
            aria-hidden
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: verified ? "#e8f3eb" : "#eef4fb",
              color: verified ? "#2f6b3a" : "#254069",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              margin: "0 auto 16px",
            }}
          >
            {verified ? "✓" : "⏳"}
          </div>
          <h3 style={{ margin: "0 0 8px", color: "#16181d" }}>
            {verified ? "You're verified" : "We're finalising your check"}
          </h3>
          <p style={{ color: "#6b6f78", margin: "0 auto 20px", maxWidth: 420 }}>
            {verified
              ? "Everything looks good. Continue to the next step."
              : "Most reviews finish in under a minute. We're checking automatically — press Continue when you're ready, or we'll move you on as soon as it's done."}
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <button
              onClick={checkAgain}
              disabled={checkingAgain}
              className="orenda-btn-primary"
            >
              {checkingAgain ? "Checking…" : "Continue"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const sumsubHostStyle = {
  border: "1px solid #d8d4cb",
  background: "#fafaf7",
  borderRadius: 6,
  marginTop: 16,
  minHeight: 640,
  overflow: "hidden",
};

export default OrendaSumsub;
